import { createApi } from '@reduxjs/toolkit/query/react';
import {
  UserAuth,
  UserAuthResponse,
  RegisteredUserAuthResponse,
  CompanyRegistrationRequest,
  CompanyRegistrationResponse,
  SignUpCredential,
  InvitePreviewResponse,
  AcceptInviteRequest,
  AcceptInviteResponse,
  CheckUserRoleRequest,
  CheckUserRoleResponse,
  UpdateRoleRequest,
  UpdateRoleResponse,
} from '@handover/shared-types';
import { END_POINTS, AUTH_END_POINTS, METHOD } from '../../constants/constant';
import { customAuthBaseQuery } from '../customBaseQuery';

type BackendLoginRequest = {
  email?: string;
  password?: string;
  idToken?: string;
};

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: customAuthBaseQuery,
  endpoints: builder => ({
    login: builder.mutation<UserAuthResponse, BackendLoginRequest>({
      query: data => ({
        url: END_POINTS.signin,
        method: METHOD.POST,
        body: data,
        //credentials: "include",
      }),
    }),
    getSignedInUser: builder.query<{ userAuth: UserAuth }, void>({
      query: () => ({
        url: END_POINTS.signedIn,
        method: METHOD.GET,
      }),
    }),
    resetUserPassword: builder.mutation<{ message: string }, { email: string }>({
      query: data => ({
        url: END_POINTS.resetPassword,
        method: METHOD.POST,
        body: data,
      }),
    }),
    signUp: builder.mutation<RegisteredUserAuthResponse, SignUpCredential>({
      query: data => ({
        url: END_POINTS.signup,
        method: METHOD.POST,
        body: data,
      }),
    }),
    getInvitePreview: builder.query<InvitePreviewResponse, string>({
      query: token => ({
        url: END_POINTS.invitePreview,
        method: METHOD.GET,
        params: { token },
      }),
    }),
    acceptInvite: builder.mutation<AcceptInviteResponse, AcceptInviteRequest>({
      query: data => ({
        url: END_POINTS.inviteAccept,
        method: METHOD.POST,
        body: data,
      }),
    }),
    registerCompany: builder.mutation<CompanyRegistrationResponse, CompanyRegistrationRequest>({
      query: data => ({
        url: END_POINTS.companyRegister,
        method: METHOD.POST,
        body: data,
      }),
    }),
    logoutUser: builder.mutation<void, void>({
      query() {
        return {
          url: 'logout',
          method: METHOD.POST,
          credentials: 'omit',
        };
      },
    }),
    checkUserRole: builder.mutation<CheckUserRoleResponse, CheckUserRoleRequest>({
      query: data => ({
        url: AUTH_END_POINTS.checkUserRole,
        method: METHOD.POST,
        body: data,
        credentials: 'omit',
      }),
    }),
    updateUserRole: builder.mutation<UpdateRoleResponse, UpdateRoleRequest>({
      query: data => ({
        url: AUTH_END_POINTS.updateRole,
        method: METHOD.PUT,
        body: data,
        credentials: 'omit',
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useGetSignedInUserQuery,
  useResetUserPasswordMutation,
  useSignUpMutation,
  useGetInvitePreviewQuery,
  useAcceptInviteMutation,
  useRegisterCompanyMutation,
  useLogoutUserMutation,
  useCheckUserRoleMutation,
  useUpdateUserRoleMutation,
} = authApi;
